import styled from '@emotion/styled'
import { useMachine } from '@xstate/react'
import * as React from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { useUpdatedRef } from '~logic/hooks/useUpdatedRef'
import { scrollOverflowMachine } from '~logic/xstate/machines/ui/scrollOverflowMachine'
import { TScrollOverflowMachineTypes } from '~logic/xstate/machines/ui/scrollOverflowMachineTypes'
import { Box, Flex, Grid } from '~ui/components/layout/Box'
import { noScrollbarStyles } from '~ui/components/layout/NoScrollbarBox'

import { ScrollOverflowButton } from './ScrollOverflowButton'

type TRenderChildrenOptions = {
  /**
   * value which is passed to the child component where it serves as the id (HTMLElement id attribute) of the direct parent of items (such as buttons or other elements that should possibly be centered)
   */
  itemContainerElementId: string
}

type TChildrenProps =
  | {
      /**
       * if true, provide children as a render function
       */
      withNavContainer?: false | undefined
      children: React.ReactNode
    }
  | {
      /**
       * if true, provide children as a render function (the function gets options object with the id to be used by children)
       */
      withNavContainer: true
      children: (options: TRenderChildrenOptions) => React.ReactNode
    }

type TProps = TChildrenProps & {
  /**
   * index of the element that should be centered within the container (e.g. the selected Rolling Options button)
   */
  selectedElementIndex?: number
  /**
   * ScrollOverflow id, used to identify the component and it's content, if there is more ScrollOverflows rendered at the same time
   */
  id: Nullable<string>
  /**
   * indicator of data change which may affect the calculated sizes
   */
  recalculateIndicator?: any
  /**
   * Use if you need to align scroll buttons other than center.
   * @example {position: `top`, offset:`calc(50px - 50%)`} // aligns the buttons center 50px from the component top
   */
  buttonPosition?: PropsOf<typeof ScrollOverflowButton>['buttonPosition']
  /**
   * Whether the scroll buttons should be rendered, regardless of whether the touch device or not. If `true` buttons will always be rendered.
   */
  withScrollButtons?: boolean
  /**
   * how much (px) the content should scroll when you click the scroll button
   */
  buttonShiftValue?: number
}

type TStyledContentProps = {
  scrollBehavior: Defined<React.CSSProperties['scrollBehavior']>
}

const propForwardOptions = {
  shouldForwardProp: (propName: string) => propName !== `scrollBehavior`,
}

const StyledContent = styled(Box, propForwardOptions)<TStyledContentProps>`
  ${noScrollbarStyles}

  position: relative;
  overflow: auto;
  scroll-behavior: ${({ scrollBehavior }) => scrollBehavior};
`

// resize handler debounce period
const RESIZE_DELAY_MS = 250

// debounce period of send('CENTER_CHILD',...)
const CENTER_CHILD_DELAY_MS = 250

export const ScrollOverflow = ({
  id,
  selectedElementIndex,
  withScrollButtons = false,
  buttonShiftValue,
  buttonPosition,
  recalculateIndicator,
  ...childrenProps
}: TProps) => {
  const itemContainerElementId = `ScrollOverflow${id}ItemContainer`

  const refContent = React.useRef<HTMLDivElement>(null)
  const refContainer = React.useRef<HTMLDivElement>(null)

  // interpret the machine with the initial context (content and container refs)
  const [state, send] = useMachine(scrollOverflowMachine, {
    context: {
      contentElementRef: refContent,
      containerElementRef: refContainer,
      itemContainerElementId,
    },
  })

  const refState = useUpdatedRef(state)

  // when resizing the window, send the 'CALC_DIMENSIONS' event to the machine only if the window size does not change for the specified amount of time
  const debouncedResizeHandler = useDebouncedCallback(
    () => send('CALC_DIMENSIONS'),
    RESIZE_DELAY_MS,
  )

  /**
   * When the selected element is changed multiple times over a short period (e.g. scrolling over ware categories with just a few recipes each),
   * center the last selected element and do not bother to center middle ones as it could cause flickering and unwanted shifts.
   *
   * Option `leading: true` ensures centering without delay if only one element is selected.
   * https://github.com/xnimorz/use-debounce#options
   */
  const debouncedCenterChild = useDebouncedCallback(
    (selectedElementIndex) => {
      send('CENTER_CHILD', { payload: { childIndex: selectedElementIndex } })
    },
    CENTER_CHILD_DELAY_MS,
    { leading: true },
  )

  React.useEffect(() => {
    // let the machine do the initial calculations and start managing the content position
    send('CALC_DIMENSIONS')

    window.addEventListener('resize', debouncedResizeHandler)

    return () => {
      // remove the resize listener when the component is unmounted
      window.removeEventListener('resize', debouncedResizeHandler)
    }
  }, [debouncedResizeHandler, send, recalculateIndicator])

  // when selected element changes, try to center it
  React.useEffect(() => {
    if (refState.current.matches('init') || refState.current.matches('off')) {
      return
    }

    if (typeof selectedElementIndex !== 'number' || selectedElementIndex < 0) {
      // no element selected
      return
    }

    debouncedCenterChild(selectedElementIndex)
  }, [send, selectedElementIndex, debouncedCenterChild, refState])

  // when `scrollValue` changes, set it as `scrollLeft` property of the content
  React.useEffect(() => {
    if (refContent.current === null) {
      return
    }

    refContent.current.scrollLeft = state.context.scrollValue
  }, [state.context.scrollValue])

  const renderScrollButtons = () => {
    if (refState.current.matches(`off`)) {
      return null
    }

    // to conditionally (based on the machine state) render scroll buttons
    const tagLeftShiftPossible: TScrollOverflowMachineTypes['tags'] = 'leftShiftPossible'
    const tagRightShiftPossible: TScrollOverflowMachineTypes['tags'] = 'rightShiftPossible'
    const isLeftShiftPossible = state.hasTag(tagLeftShiftPossible)
    const isRightShiftPossible = state.hasTag(tagRightShiftPossible)

    // https://stackoverflow.com/questions/56324813/how-to-detect-touch-device-in-2019
    const isNonTouchDevice = !(
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      matchMedia('(hover: none)').matches
    )

    if (withScrollButtons || isNonTouchDevice) {
      const defaultShiftValue =
        refContainer.current !== null && refContainer.current.clientWidth
          ? refContainer.current.clientWidth * 0.9
          : 0

      const shiftValue = buttonShiftValue ?? defaultShiftValue

      return (
        <>
          {isLeftShiftPossible && (
            <ScrollOverflowButton
              buttonPosition={buttonPosition}
              buttonType={`left`}
              onClick={() =>
                send({
                  type: 'SHIFT',
                  payload: { shiftValue: shiftValue * -1 },
                })
              }
            />
          )}
          {isRightShiftPossible && (
            <ScrollOverflowButton
              buttonPosition={buttonPosition}
              buttonType={`right`}
              onClick={() => send({ type: 'SHIFT', payload: { shiftValue } })}
            />
          )}
        </>
      )
    }

    return null
  }

  return (
    <Flex position={`relative`} alignItems={`center`}>
      <Grid ref={refContainer}>
        <StyledContent ref={refContent} scrollBehavior={`smooth`}>
          {childrenProps.withNavContainer
            ? childrenProps.children({ itemContainerElementId })
            : childrenProps.children}
        </StyledContent>
      </Grid>

      {renderScrollButtons()}
    </Flex>
  )
}
